import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../lib/errors';
import { type LeadRow } from '../types';
import LeadModal from './LeadModal';

const STAGES = [
  { key: 'novo_contato', label: 'Novo contato', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'orcamento_enviado', label: 'Orçamento enviado', color: 'bg-[#E8610A]/10 text-[#E8610A] border-[#E8610A]/20' },
  { key: 'em_negociacao', label: 'Em negociação', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  { key: 'ganho', label: 'Ganho', color: 'bg-green-50 text-green-700 border-green-200' },
  { key: 'perdido', label: 'Perdido', color: 'bg-gray-100 text-gray-600 border-gray-200' },
];

const WON_STATUSES = ['fechado', 'pago', 'entregue'];

interface FunilVendasProps {
  password: string;
  refreshKey: number;
  onChanged: () => void;
}

interface LeadWithProgress extends LeadRow {
  won_value: number;
  open_value: number;
}

interface FunnelCard {
  lead: LeadWithProgress;
  value: number;
  tag?: string;
}

interface QuoteMin {
  id: number;
  lead_id: number | null;
}

interface OrderMin {
  quote_id: number | null;
  status: string;
  subtotal_items: number | null;
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function FunilVendas({ password, refreshKey, onChanged }: FunilVendasProps) {
  const [leads, setLeads] = useState<LeadWithProgress[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalLead, setModalLead] = useState<LeadRow | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [leadsRes, quotesRes, ordersRes] = await Promise.all([
        supabase.functions.invoke('crm-admin', { body: { action: 'list_leads', password } }),
        supabase.functions.invoke('crm-admin', { body: { action: 'list_quotes', password } }),
        supabase.functions.invoke('crm-admin', { body: { action: 'list_quote_orders', password } }),
      ]);

      const leadParsed = leadsRes?.data as { leads?: LeadRow[]; error?: string };
      if (leadsRes?.error || leadParsed?.error) {
        const msg = leadsRes?.error ? await extractErrorMessage(leadsRes.error) : leadParsed?.error;
        throw new Error(msg ?? 'Erro ao carregar leads.');
      }

      const quotesParsed = quotesRes?.data as { quotes?: QuoteMin[]; error?: string } | undefined;
      const ordersParsed = ordersRes?.data as { orders?: OrderMin[]; error?: string } | undefined;

      // Mapeia orçamento -> lead para descobrir o que já foi vendido.
      const quoteLeadMap: Record<string, number> = {};
      (quotesParsed?.quotes ?? []).forEach((q) => {
        if (q?.id != null && q?.lead_id != null) {
          quoteLeadMap[String(q.id)] = Number(q.lead_id);
        }
      });

      const wonByLead: Record<string, number> = {};
      (ordersParsed?.orders ?? []).forEach((o) => {
        if (!WON_STATUSES.includes(o?.status)) return;
        if (o?.quote_id == null) return;
        const leadId = quoteLeadMap[String(o.quote_id)];
        if (leadId != null) {
          wonByLead[String(leadId)] = (wonByLead[String(leadId)] ?? 0) + Number(o?.subtotal_items ?? 0);
        }
      });

      const enriched: LeadWithProgress[] = (leadParsed?.leads ?? []).map((l) => {
        const value = Number(l.value ?? 0);
        const won = wonByLead[String(l.id)] ?? 0;
        return {
          ...l,
          won_value: won,
          open_value: Math.max(0, value - won),
        };
      });

      setLeads(enriched);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar leads.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const cardsByStage = (): Record<string, FunnelCard[]> => {
    const result: Record<string, FunnelCard[]> = {};
    STAGES.forEach((s) => {
      result[s.key] = [];
    });

    leads.forEach((lead) => {
      const value = Number(lead.value ?? 0);
      const won = lead.won_value;
      const open = lead.open_value;

      if (lead.stage === 'ganho') {
        result.ganho.push({ lead, value });
      } else if (lead.stage === 'perdido') {
        result.perdido.push({ lead, value });
      } else {
        if (won > 0) {
          result.ganho.push({ lead, value: won, tag: open > 0 ? 'Parte vendida' : undefined });
        }
        if (open > 0) {
          result[lead.stage]?.push({ lead, value: open, tag: won > 0 ? 'Restante' : undefined });
        }
      }
    });

    return result;
  };

  const cards = cardsByStage();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
            <i className="ri-filter-3-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Funil de vendas</h2>
            <p className="text-sm text-gray-500">{leads.length} leads no total</p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Atualizar
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {STAGES.map((stage) => {
          const stageCards = cards[stage.key] ?? [];
          const stageTotal = stageCards.reduce((acc, c) => acc + c.value, 0);
          return (
            <div key={stage.key} className="bg-white rounded-xl border border-hop-border p-4">
              <div className={`inline-flex items-center gap-1.5 text-xs font-semibold border rounded-full px-2.5 py-1 mb-3 ${stage.color}`}>
                {stage.label}
                <span className="bg-white/60 rounded-full px-1.5 py-0.5 text-[10px]">{stageCards.length}</span>
              </div>
              {stageTotal > 0 && (
                <p className="text-sm font-bold text-gray-900 mb-3">{formatBRL(stageTotal)}</p>
              )}
              <div className="space-y-2">
                {stageCards.map((card) => {
                  const lead = card.lead;
                  const isOverdue = lead.next_follow_up
                    ? new Date(lead.next_follow_up).getTime() < new Date().getTime()
                    : false;
                  return (
                    <div
                      key={`${lead.id}-${card.tag ?? 'full'}`}
                      onClick={() => setModalLead(lead)}
                      className="p-3 bg-hop-bg-alt rounded-lg border border-hop-border cursor-pointer hover:border-[#E8610A]/40 transition-colors"
                    >
                      <p className="text-sm font-semibold text-gray-900 truncate">{lead.name}</p>
                      <p className="text-xs text-gray-500">{lead.phone || lead.email || 'Sem contato'}</p>
                      <p className="text-xs font-semibold text-gray-700 mt-1">
                        {formatBRL(card.value)}
                      </p>
                      {card.tag && (
                        <span className={`inline-block mt-1 text-[10px] font-semibold rounded-full px-2 py-0.5 ${
                          card.tag === 'Parte vendida'
                            ? 'bg-green-50 text-green-700'
                            : 'bg-amber-50 text-amber-700'
                        }`}>
                          {card.tag}
                        </span>
                      )}
                      {isOverdue && (
                        <span className="inline-block mt-1 ml-1 text-[10px] font-semibold bg-red-50 text-red-600 rounded-full px-2 py-0.5">
                          Follow-up atrasado
                        </span>
                      )}
                    </div>
                  );
                })}
                {stageCards.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-4">Nenhum lead</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {modalLead && (
        <LeadModal
          lead={modalLead}
          password={password}
          onClose={() => setModalLead(null)}
          onChanged={async () => {
            setModalLead(null);
            await load();
            onChanged();
          }}
        />
      )}
    </div>
  );
}